import * as React from "react";
import { cn } from "../lib/cn";
import { CopyButton } from "./copy";

export interface SnippetVariant {
  id: string;
  label: string;
  code: string;
}

/**
 * Code snippet panel (ui-spec §3.6): monochrome, horizontal scroll, language tabs and Copy. Snippet text comes from
 * the shared template registry and the object's own values, so it is always runnable; `$NODUS_API_KEY` stands in
 * for a secret, which is never inserted.
 */
export function CodeSnippet({
  variants,
  initial,
  header,
  className,
}: {
  variants: SnippetVariant[];
  initial?: string;
  /** Extra controls in the header row, such as the "Use key" select. */
  header?: React.ReactNode;
  className?: string;
}) {
  const [active, setActive] = React.useState(initial ?? variants[0]?.id ?? "");
  const current = variants.find((v) => v.id === active) ?? variants[0];
  return (
    <div className={cn("rounded-lg border border-line bg-sunken", className)}>
      <div className="flex items-center gap-3 border-b border-line-subtle px-2">
        {variants.length > 1 ? (
          <div role="group" aria-label="Language" className="flex gap-0.5 py-1">
            {variants.map((v) => (
              <button
                key={v.id}
                type="button"
                aria-pressed={v.id === active}
                onClick={() => setActive(v.id)}
                className="h-7 rounded-sm px-2.5 text-ui text-fg-muted hover:text-fg-primary aria-pressed:bg-selected aria-pressed:text-fg-primary"
              >
                {v.label}
              </button>
            ))}
          </div>
        ) : (
          <span className="px-1 py-2 text-small text-fg-muted">{current?.label}</span>
        )}
        <div className="ml-auto flex items-center gap-2 py-1">
          {header}
          {current ? <CopyButton text={current.code} label="Copy" /> : null}
        </div>
      </div>
      <pre tabIndex={0} aria-label={`${current?.label ?? "Code"} snippet`} className="overflow-x-auto px-4 py-3 font-mono text-mono leading-6 text-fg-body">
        <code>{current?.code}</code>
      </pre>
    </div>
  );
}
